import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Collapse,
  Stack,
  Divider
} from '@mui/material';
import useAuth from '../../../hooks/useAuth';
import operationsService from '../../../services/operationsService';
import unifiedSocket from '../../../services/unifiedSocket';
import slaService from '../../../services/slaService';

const STATUS_ORDER = [
  'new',
  'triage',
  'queued',
  'assigned',
  'pending_customer',
  'pending_internal',
  'resolved',
  'closed',
  'reopened'
];

function statusChipColor(status) {
  if (status === 'assigned') return 'primary';
  if (status === 'queued' || status === 'triage' || status === 'pending_customer' || status === 'pending_internal') return 'warning';
  if (status === 'resolved' || status === 'closed') return 'success';
  if (status === 'reopened') return 'info';
  return 'default';
}

export default function WhatsAppOperations() {
  const { user: authUser } = useAuth();
  const currentUser = authUser?.user || authUser || null;
  const token = authUser?.token || null;
  const socketUserId = currentUser?.id || null;
  const companyId = currentUser?.companyId || 1;

  const [queues, setQueues] = useState([]);
  const [operators, setOperators] = useState([]);
  const [pauseReasons, setPauseReasons] = useState([]);
  const [expandedQueues, setExpandedQueues] = useState(new Set());

  const [settingsDialog, setSettingsDialog] = useState({ open: false, queue: null });
  const [settingsForm, setSettingsForm] = useState({
    assignmentMode: 'manual',
    assignmentStrategy: 'round_robin',
    maxSimultaneousPerAgent: 5,
    slaPolicyId: ''
  });
  const [slaPolicies, setSlaPolicies] = useState([]);

  const loadData = async () => {
    const [q, o, r, p] = await Promise.all([
      operationsService.getQueuesSummary(),
      operationsService.getOperatorsSummary(),
      operationsService.listPauseReasons(),
      slaService.listPolicies().catch(() => null)
    ]);
    setQueues(q?.queues || q?.data?.queues || []);
    setOperators(o?.operators || o?.data?.operators || []);
    setPauseReasons(r?.reasons || r?.data?.reasons || []);
    setSlaPolicies(p?.policies || p?.data?.policies || []);
  };

  useEffect(() => {
    loadData().catch(() => {});
  }, []);

  useEffect(() => {
    if (!token || !socketUserId) return;
    unifiedSocket.connect({ id: socketUserId, companyId, token });
    const socket = unifiedSocket.getSocket?.();
    if (!socket) return;

    const onSnapshot = (payload) => {
      if (!payload) return;
      if (Array.isArray(payload.queues)) {
        setQueues(payload.queues);
      }
      if (Array.isArray(payload.operators)) {
        setOperators(payload.operators.map(o => ({
          id: o.userId,
          name: o.name,
          type: 'agent',
          presence: { status: o.status, pauseReasonId: o.pauseReasonId, updatedAt: o.updatedAt }
        })));
      }
    };

    socket.on('ops.snapshot', onSnapshot);
    const onPresence = (p) => {
      setOperators(prev => {
        const exists = prev.find(x => x.id === p.userId);
        if (!exists) return prev;
        return prev.map(x => x.id === p.userId ? { ...x, presence: { status: p.status, pauseReasonId: p.pauseReasonId, updatedAt: p.updatedAt } } : x);
      });
    };
    socket.on('operator.presence', onPresence);
    return () => {
      try { socket.off('ops.snapshot', onSnapshot); } catch {}
      try { socket.off('operator.presence', onPresence); } catch {}
    };
  }, [token, socketUserId, companyId]);

  const pauseReasonById = useMemo(() => new Map(pauseReasons.map(r => [r.id, r])), [pauseReasons]);

  const toggleQueue = (id) => {
    setExpandedQueues(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box>
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" fontWeight="bold">Operação (Filas e Operadores)</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => operationsService.setMyPresence('online').then(loadData).catch(() => {})}>Online</Button>
          <Button variant="outlined" onClick={() => operationsService.setMyPresence('offline').then(loadData).catch(() => {})}>Offline</Button>
          <Button variant="outlined" onClick={() => loadData().catch(() => {})}>Atualizar</Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Filas</Typography>
            <Stack spacing={2}>
              {queues.map(q => {
                const qid = q.id || q.queueId;
                const backlog = q.backlog || {};
                const expanded = expandedQueues.has(qid);
                const total = STATUS_ORDER.reduce((acc, s) => acc + Number(backlog[s] || 0), 0);
                const mainStatuses = ['new', 'triage', 'queued', 'assigned', 'pending_customer', 'pending_internal', 'reopened'];
                return (
                  <Paper key={qid} variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                      <Box minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <span style={{ width: 10, height: 10, borderRadius: 999, background: q.color || '#3b82f6', display: 'inline-block' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {q.name}
                          </Typography>
                          <Chip size="small" label={`Total ${total}`} variant="outlined" />
                          <Chip size="small" label={`SLA vencidos ${q.overdue || 0}`} color={(q.overdue || 0) > 0 ? 'error' : 'default'} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Modo: {q.settings?.assignmentMode || '-'} · Estratégia: {q.settings?.assignmentStrategy || '-'} · Limite: {q.settings?.maxSimultaneousPerAgent ?? '-'}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                        <Button size="small" variant="outlined" onClick={() => toggleQueue(qid)}>
                          {expanded ? 'Ocultar' : 'Detalhes'}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            setSettingsDialog({ open: true, queue: q });
                            setSettingsForm({
                              assignmentMode: q.settings?.assignmentMode || 'manual',
                              assignmentStrategy: q.settings?.assignmentStrategy || 'round_robin',
                              maxSimultaneousPerAgent: q.settings?.maxSimultaneousPerAgent ?? 5,
                              slaPolicyId: q.settings?.slaPolicyId ? String(q.settings.slaPolicyId) : ''
                            });
                          }}
                        >
                          Configurar
                        </Button>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {mainStatuses.map(s => (
                        <Chip key={s} size="small" label={`${s}: ${backlog?.[s] || 0}`} color={statusChipColor(s)} />
                      ))}
                    </Box>
                    <Collapse in={expanded}>
                      <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                        {STATUS_ORDER.map(s => (
                          <Chip key={s} size="small" label={`${s}: ${backlog?.[s] || 0}`} color={statusChipColor(s)} />
                        ))}
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
              {!queues.length ? <Typography variant="body2" color="text.secondary">Sem filas ativas.</Typography> : null}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Operadores</Typography>
            <Stack spacing={1}>
              {operators.map(op => (
                <Paper key={op.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {op.name}
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                        <Chip size="small" label={op.presence?.status || 'offline'} color={op.presence?.status === 'online' ? 'success' : op.presence?.status === 'busy' ? 'primary' : op.presence?.status === 'paused' ? 'warning' : 'default'} />
                        <Chip size="small" label={`Carga ${op.load ?? 0}`} variant="outlined" />
                        {op.presence?.pauseReasonId ? (
                          <Chip size="small" label={pauseReasonById.get(op.presence.pauseReasonId)?.name || 'Pausa'} color="warning" variant="outlined" />
                        ) : null}
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {op.presence?.updatedAt ? new Date(op.presence.updatedAt).toLocaleString() : '—'}
                    </Typography>
                  </Box>
                </Paper>
              ))}
              {!operators.length ? <Typography variant="body2" color="text.secondary">Sem operadores.</Typography> : null}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={settingsDialog.open} onClose={() => setSettingsDialog({ open: false, queue: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar fila</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Modo</InputLabel>
              <Select
                label="Modo"
                value={settingsForm.assignmentMode}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, assignmentMode: e.target.value }))}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="auto">Automático</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Estratégia</InputLabel>
              <Select
                label="Estratégia"
                value={settingsForm.assignmentStrategy}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, assignmentStrategy: e.target.value }))}
              >
                <MenuItem value="round_robin">Round robin</MenuItem>
                <MenuItem value="least_load">Menor carga</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Limite simultâneo por agente"
              type="number"
              value={settingsForm.maxSimultaneousPerAgent}
              onChange={(e) => setSettingsForm(prev => ({ ...prev, maxSimultaneousPerAgent: Number(e.target.value) }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Política de SLA</InputLabel>
              <Select
                label="Política de SLA"
                value={settingsForm.slaPolicyId}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, slaPolicyId: e.target.value }))}
              >
                <MenuItem value="">Padrão</MenuItem>
                {slaPolicies.map(p => (
                  <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog({ open: false, queue: null })}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const q = settingsDialog.queue;
              if (!q) return;
              await operationsService.updateQueueSettings(q.id || q.queueId, {
                ...settingsForm,
                slaPolicyId: settingsForm.slaPolicyId ? Number(settingsForm.slaPolicyId) : null
              });
              setSettingsDialog({ open: false, queue: null });
              await loadData();
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
